import {
  CellProps,
  usePagination,
  useTable,
  Text,
  Badge,
  useForm,
  Flex,
  Button,
  FiCheckCircle,
  CrossOutlineIcon,
  FileAttachmentErrorIcon,
  useDisclosure,
  FiEye,
} from '@agrotoken-tech/ui';
import { useDebounce, useTranslation, useToastsHook } from '@bcba/react-hooks';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { ExplorerListReceivedDigitalDossierAccessRequestsSerializer } from '@bcba/serializers';
import { accessRequestStatusBadgeColors } from '@bcba/frontend-utils';
import {
  formatDateToShortFormat,
  unixTimestampToDate,
} from '@bcba/common-utils';
import {
  DIGITAL_DOSSIER_ACCESS_REQUEST_STATUS,
  type DigitalDossierAccessRequestStatus,
  digitalDossierAccessRequestStatuses,
  type DigitalDossierSection,
} from '@bcba/types';
import {
  approveReceivedAccessRequest,
  getReceivedDigitalDossierAccessRequests,
  rejectReceivedAccessRequest,
  revokeAccessReceivedAccessRequest,
} from '../../../services/digital-dossier-access-requests/receivedAccessRequests';
import React from 'react';
import { updateAccessibleSectionsByUuid } from '../../../services/digital-dossier-access-requests';

export const useReceivedDigitalDossierAccessRequestsTable = () => {
  const { t } = useTranslation();
  const { showSuccessToast, showErrorToast } = useToastsHook();
  const formMethods = useForm({ defaultValues: { search: '' } });
  const [page, setPage] = useState(1);
  const { watch, setValue } = formMethods;
  const search = watch('search');
  const debouncedSearch = useDebounce(search, 300);

  const [selectedSections, setSelectedSections] = useState<
    DigitalDossierSection[]
  >([]);
  const [selectedCompanyName, setSelectedCompanyName] = useState<string | null>(
    null,
  );
  const [approvedRequestUuid, setApprovedRequestUuid] = useState<string | null>(
    null,
  );
  const [rejectedRequestUuid, setRejectedRequestUuid] = useState<string | null>(
    null,
  );
  const [revokedRequestUuid, setRevokedRequestUuid] = useState<string | null>(
    null,
  );
  const [updateAccessibleSectionUuid, setUpdateAccessibleSectionUuid] =
    useState<string>('');
  const [selectedStatuses, setSelectedStatuses] = useState<
    DigitalDossierAccessRequestStatus[]
  >([]);
  const debouncedStatus = useDebounce(selectedStatuses, 300);

  const statusOptions = digitalDossierAccessRequestStatuses.map((s) => ({
    text: t(`receivedAccessRequests.status.${s}`),
    value: s,
  }));

  const {
    isOpen: isOpenApproveModal,
    onOpen: onOpenApproveModal,
    onClose: onCloseApproveModal,
  } = useDisclosure();
  const {
    isOpen: isOpenRejectModal,
    onOpen: onOpenRejectModal,
    onClose: onCloseRejectModal,
  } = useDisclosure();
  const {
    isOpen: isOpenRevokeModal,
    onOpen: onOpenRevokeModal,
    onClose: onCloseRevokeModal,
  } = useDisclosure();
  const {
    isOpen: isOpenAccessibleSectionModal,
    onOpen: onOpenAccessibleSectionModal,
    onClose: onCloseAccessibleSectionModal,
  } = useDisclosure();

  const { data, isLoading, refetch } = useQuery({
    queryKey: [
      'receivedDigitalDossierAccessRequests',
      page,
      debouncedSearch,
      debouncedStatus,
    ],
    queryFn: () =>
      getReceivedDigitalDossierAccessRequests({
        page,
        search: debouncedSearch,
        statuses: debouncedStatus,
      }),
    enabled: debouncedSearch !== undefined && debouncedStatus !== undefined,
  });

  const {
    mutate: approvesReceivedAccessRequest,
    isPending: isPendingApproveRequest,
  } = useMutation({
    mutationFn: async () => {
      if (approvedRequestUuid) {
        return approveReceivedAccessRequest(
          approvedRequestUuid,
          selectedSections,
        );
      }
    },
    onSuccess: () => {
      showSuccessToast({
        title: t('receivedAccessRequests.approveRequest.success.title'),
      });
      refetch();
      onCloseApproveModal();
    },
    onError: () => {
      showErrorToast({
        title: t('common.toast.error.title'),
        text: t('common.toast.error.text'),
      });
      onCloseApproveModal();
      refetch();
    },
  });

  const {
    mutate: rejectsReceivedAccessRequest,
    isPending: isPendingRejectRequest,
  } = useMutation({
    mutationFn: async () => {
      if (rejectedRequestUuid) {
        return rejectReceivedAccessRequest(rejectedRequestUuid);
      }
    },
    onSuccess: () => {
      showSuccessToast({
        title: t('receivedAccessRequests.rejectRequest.success.title'),
      });
      refetch();
      onCloseRejectModal();
    },
    onError: () => {
      showErrorToast({
        title: t('common.toast.error.title'),
        text: t('common.toast.error.text'),
      });
      onCloseRejectModal();
      refetch();
    },
  });

  const {
    mutate: revokesReceivedAccessRequest,
    isPending: isPendingRevokeRequest,
  } = useMutation({
    mutationFn: async () => {
      if (revokedRequestUuid) {
        return revokeAccessReceivedAccessRequest(revokedRequestUuid);
      }
    },
    onSuccess: () => {
      showSuccessToast({
        title: t('receivedAccessRequests.revokeRequest.success.title'),
        text: t('signupRequestDetail.approve.success.text'),
      });
      refetch();
      onCloseRevokeModal();
    },
    onError: () => {
      showErrorToast({
        title: t('common.toast.error.title'),
        text: t('common.toast.error.text'),
      });
      onCloseRevokeModal();
      refetch();
    },
  });

  const {
    mutate: updateAccessibleSections,
    isPending: isLoadingMutateAccessibleSection,
  } = useMutation({
    mutationFn: async () =>
      updateAccessibleSectionsByUuid(updateAccessibleSectionUuid, {
        accessibleSections: selectedSections,
      }),
    onSuccess: () => {
      showSuccessToast({
        title: t(
          'receivedAccessRequests.accessibleSections.toast.success.title',
        ),
      });
      refetch();
      onCloseAccessibleSectionModal();
    },
    onError: () => {
      showErrorToast({
        title: t('receivedAccessRequests.accessibleSections.toast.error.title'),
      });
    },
  });

  const columns = useMemo(
    () => [
      {
        Header: t('receivedAccessRequests.table.requestedBy'),
        accessor: 'requestingCompanyName',
      },
      {
        Header: t('receivedAccessRequests.table.companyFiscalId'),
        accessor: 'requestingCompanyFiscalId',
      },
      {
        Header: t('receivedAccessRequests.table.requestedAt'),
        accessor: 'requestedAt',
        Cell: (
          props: CellProps<ExplorerListReceivedDigitalDossierAccessRequestsSerializer>,
        ) => {
          const { requestedAt } = props.row.original;
          return (
            <Text size="sm">
              {requestedAt
                ? formatDateToShortFormat(unixTimestampToDate(requestedAt))
                : '-'}
            </Text>
          );
        },
      },
      {
        Header: t('receivedAccessRequests.table.requestStatus'),
        accessor: 'requestStatus',
        Cell: (
          props: CellProps<ExplorerListReceivedDigitalDossierAccessRequestsSerializer>,
        ) => {
          const { requestStatus } = props.row.original;
          return (
            <Badge colorScheme={accessRequestStatusBadgeColors[requestStatus]}>
              {t(`receivedAccessRequests.status.${requestStatus}`)}
            </Badge>
          );
        },
      },
      {
        Header: t('receivedAccessRequests.table.accessibleSections'),
        accessor: 'accessibleSections',
        Cell: (
          props: CellProps<ExplorerListReceivedDigitalDossierAccessRequestsSerializer>,
        ) => {
          const { uuid, requestStatus, accessibleSections } =
            props.row.original;

          const isApproved =
            requestStatus === DIGITAL_DOSSIER_ACCESS_REQUEST_STATUS.APPROVED;
          return isApproved ? (
            <Button
              variant={'linkSecondary'}
              leftIcon={<FiEye />}
              size={'sm'}
              onClick={() => {
                setUpdateAccessibleSectionUuid(uuid);
                setSelectedSections(accessibleSections);
                onOpenAccessibleSectionModal();
              }}
            >
              {t('receivedAccessRequests.table.accessibleSections.button')}
            </Button>
          ) : (
            '-'
          );
        },
      },
      {
        Header: (
          <Flex
            width={'full'}
            justifyContent={{ base: 'start', md: 'end' }}
            data-header-text={t('receivedAccessRequests.table.actions')}
          >
            {t('receivedAccessRequests.table.actions')}
          </Flex>
        ),
        accessor: 'uuid',
        Cell: (
          props: CellProps<ExplorerListReceivedDigitalDossierAccessRequestsSerializer>,
        ) => {
          const { uuid, requestStatus, requestingCompanyName } =
            props.row.original;
          const isPendingApproval =
            requestStatus === DIGITAL_DOSSIER_ACCESS_REQUEST_STATUS.PENDING;
          const isApproved =
            requestStatus === DIGITAL_DOSSIER_ACCESS_REQUEST_STATUS.APPROVED;
          return (
            <Flex justifyContent={{ base: 'start', md: 'end' }} gap={3}>
              {isPendingApproval && (
                <>
                  <Button
                    variant={'linkPrimary'}
                    leftIcon={<FiCheckCircle />}
                    size={'sm'}
                    onClick={() => {
                      setApprovedRequestUuid(uuid);
                      setSelectedCompanyName(requestingCompanyName);
                      onOpenApproveModal();
                    }}
                  >
                    {t('common.approve')}
                  </Button>
                  <Button
                    variant={'linkDestructive'}
                    leftIcon={<CrossOutlineIcon />}
                    size={'sm'}
                    onClick={() => {
                      setRejectedRequestUuid(uuid);
                      setSelectedCompanyName(requestingCompanyName);
                      onOpenRejectModal();
                    }}
                  >
                    {t('common.reject')}
                  </Button>
                </>
              )}
              {isApproved && (
                <Button
                  variant={'linkDestructive'}
                  leftIcon={<FileAttachmentErrorIcon />}
                  size={'sm'}
                  onClick={() => {
                    setRevokedRequestUuid(uuid);
                    setSelectedCompanyName(requestingCompanyName);
                    onOpenRevokeModal();
                  }}
                >
                  {t('common.revokeAccess')}
                </Button>
              )}
            </Flex>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const tableData = React.useMemo(
    () => data?.data || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(data)],
  );

  const tableInstance = useTable(
    {
      columns: columns as any,
      data: tableData,
    },
    usePagination,
  );

  const handleChangeAccessibleSections = (
    sections: DigitalDossierSection[],
  ) => {
    setSelectedSections(sections);
  };

  const onFiltersReset = () => {
    setValue('search', '');
    setSelectedStatuses([]);
  };

  useEffect(() => setPage(1), [debouncedSearch, debouncedStatus]);

  return {
    data,
    formMethods,
    isLoading,
    setPage,
    tableInstance,
    selectedCompanyName,
    isOpenApproveModal,
    onCloseApproveModal,
    approvesReceivedAccessRequest,
    isPendingApproveRequest,
    isOpenRejectModal,
    onCloseRejectModal,
    rejectsReceivedAccessRequest,
    isPendingRejectRequest,
    isOpenRevokeModal,
    onCloseRevokeModal,
    revokesReceivedAccessRequest,
    isPendingRevokeRequest,
    isOpenAccessibleSectionModal,
    onCloseAccessibleSectionModal,
    accessibleSections: selectedSections,
    handleChangeAccessibleSections,
    updateAccessibleSections,
    isLoadingMutateAccessibleSection,
    selectedStatuses,
    statusOptions,
    onFiltersReset,
    setSelectedStatuses,
  };
};
